import { IconArrowRight } from "@tabler/icons-react";
import classNames from "classnames";
import dayjs from "dayjs";
import { useState } from "react";
import { useActionCenter } from "~/contexts/actionCenterProvider";
import { InboxApprovalRequest } from "~/modules/orders/OrderApprovalHistory";
import { TaskCard } from "~/modules/task/components/TaskCard";
import type { InboxListItem } from "~/server/domain/notifications";
import { Anchor, Box, Button, Modal, Text, tokens } from "~/ui";
import { api } from "~/utils/api";
import { useOrgRouter } from "~/utils/paths";
import styles from "./InboxPanel.module.css";
import {
  formatInboxRelativeTime,
  getNotificationDetailSections,
  getNotificationSeverityIcon,
  getNotificationSeverityIconColor,
  getNotificationSeverityIconSurface,
  getNotificationSeverityLabel,
  inboxMessageRowKindHasModal,
  isInboxMessageUnread,
  type NotificationSeverityLevel,
} from "./inbox-message-row";
import { handleInboxNotificationClick } from "./inbox-notification-click";
import { inboxStyleVars } from "./inbox-style-vars";

type NotificationInboxListItem = Extract<InboxListItem, { messageType: "NOTIFICATION" }>;
type TaskInboxListItem = Extract<InboxListItem, { messageType: "TASK" }>;

type InboxMessageRowProps = {
  item: InboxListItem;
  /** The list's shared clock, so relative times ("25m") stay current while the panel is open. */
  now: Date;
  /** Task mutations (edit/assign/complete/reopen/delete) call this to refresh `inbox.list` + `inbox.counts`. */
  onUpdate: () => void;
  /** Notification click (navigate + markRead) calls this to refresh `inbox.list` + `inbox.counts`. */
  onOpened: () => void;
};

/**
 * The severity avatar (SC-3700 handoff §2/§4): a round surface holding the severity icon. Severity is
 * conveyed by the icon's shape and the `title` / `aria-label` words, not by colour alone. Shared by the
 * row (32px) and the detail modal (40px) so the two cannot drift apart.
 */
const SeverityAvatar = (props: { severity: NotificationSeverityLevel; size: number; iconSize: number }): JSX.Element => {
  const SeverityIcon = getNotificationSeverityIcon(props.severity);
  const label = getNotificationSeverityLabel(props.severity);

  return (
    <div
      aria-label={label}
      className={styles.severityAvatar}
      role="img"
      style={{
        background: getNotificationSeverityIconSurface(props.severity),
        height: props.size,
        width: props.size,
      }}
      title={label}
    >
      <SeverityIcon color={getNotificationSeverityIconColor(props.severity)} size={props.iconSize} />
    </div>
  );
};

/**
 * The modal's single call to action, supplied by the caller rather than built here (round 20, per
 * user: "this should be a generic action, cause not every notification will have a link to open").
 * The detail view knows only that a notification MAY have one thing to do about it, with a label and
 * a handler; it never knows that today's only producer happens to link to an order record. A
 * notification with nothing to act on passes `null` and the footer keeps just its timestamp.
 */
type NotificationDetailAction = {
  label: string;
  onAct: () => void;
};

/**
 * Full detail shown in the notification row's modal (SC-3392 round 20 redesign, SC-3700 layout).
 *
 * It deliberately does NOT reuse the row. The row's job is to survive truncation in a narrow list; the
 * modal's job is the opposite — the reader already knows they opened a notification, so severity is one
 * avatar, the title is the headline, and the producer's `context`/`detail` become a subtitle and a body
 * paragraph: what happened, where, why, in that order, then one primary action. The close button sits in
 * the header row next to the title, so no empty band sits above it.
 */
const NotificationDetail = (props: { item: NotificationInboxListItem; action: NotificationDetailAction | null }): JSX.Element => {
  const { item } = props;
  const sections = getNotificationDetailSections(item);
  // `context` is the producer's "where this happened" line and `detail` its "why"; an older row (or a
  // source that supplies neither) falls back to the flat `message` as the body, with no subtitle.
  const subtitle = sections.kind === "sections" ? sections.context : null;
  const body = sections.kind === "sections" ? sections.detail : sections.message;

  return (
    <Modal.Body p={0}>
      <div className={styles.modalHeader}>
        <SeverityAvatar severity={item.severity} size={40} iconSize={21} />
        <div className={styles.modalTitleBlock}>
          <Modal.Title
            c={tokens.color.content.strong}
            className={styles.modalTitle}
            fw={600}
            fz="lg"
            lh={tokens.typography.textLineHeight.md}
          >
            {item.title}
          </Modal.Title>
          {subtitle && (
            <Text c={tokens.color.content.secondary} fz="sm">
              {subtitle}
            </Text>
          )}
        </div>
        <Modal.CloseButton aria-label="Close" />
      </div>
      {body && (
        <Text c={tokens.color.content.primary} className={styles.modalBody} fz="md" lh={tokens.typography.textLineHeight.md}>
          {body}
        </Text>
      )}
      <div className={styles.modalFooter}>
        <Text c={tokens.color.content.secondary} fz="sm">
          {dayjs(item.createdAt).format("MMM D, h:mm A")}
        </Text>
        {/* Round 20: whatever the caller says this notification's one action is, as a real primary
         *  `~/ui` Button rather than the bare text link it was — it is what the reader came here to do,
         *  so it gets the weight. No action means no button; opening this modal is already what marks
         *  the row read (`NotificationInboxRow`), which the old "Mark as read" link used to do. */}
        {props.action && (
          <Button rightIcon={<IconArrowRight size={14} />} size="xs" onClick={props.action.onAct}>
            {props.action.label}
          </Button>
        )}
      </div>
    </Modal.Body>
  );
};

/**
 * TASK row (SC-3392 round 18 restore, SC-3700 redesign): the inline-editable `TaskCard`
 * (`withChrome={false}`) is the whole row — leading complete toggle, title/description/due date,
 * assignee, hover-delete — with the same affordances `TaskCard` has always offered `isEditable`
 * callers, one click each, no menu and no confirm on complete/incomplete (only delete keeps its
 * confirm). It owns its row frame (padding, divider, hover region) because its absolutely-positioned
 * delete controls sit in the row's padding and must resolve against, and hover with, the full row.
 */
const TaskInboxRow = (props: { item: TaskInboxListItem; onUpdate: () => void }): JSX.Element => {
  const { task } = props.item;

  if (task.approvalRequestId !== null) {
    return <InboxApprovalRequest orderId={task.entityId} requestId={task.approvalRequestId} title={task.title} onUpdate={props.onUpdate} />;
  }

  return <TaskCard isEditable task={task} onUpdate={props.onUpdate} withChrome={false} />;
};

/**
 * NOTIFICATION row: title/message come straight off the notification, with its age and, when it has
 * one, an inline link to the record. The whole row opens a detail modal on click (round 12 UAT fix,
 * kept) — the only row type today whose body ever does (`inboxMessageRowKindHasModal`) — and is
 * keyboard reachable (Enter / Space).
 */
const NotificationInboxRow = (props: { item: NotificationInboxListItem; now: Date; onOpened: () => void }): JSX.Element => {
  const orgRouter = useOrgRouter();
  const actionCenter = useActionCenter();
  const utils = api.useContext();
  /**
   * Round 23 UAT fix ("the badge doesn't get updated"): the badge now drops the moment the row is
   * read, instead of only after the server round-trip and refetch land. `onSettled` — not `onSuccess`
   * — then refreshes from the server on BOTH outcomes, so a failed mark-read puts the optimistic
   * count straight back rather than leaving the badge permanently one too low.
   */
  const markReadMutation = api.inbox.markRead.useMutation({
    onMutate: () => {
      utils.inbox.counts.setData(undefined, current =>
        current ? { ...current, unreadNotifications: Math.max(0, current.unreadNotifications - 1) } : current,
      );
    },
    onSettled: () => props.onOpened(),
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { item } = props;
  const hasModal = inboxMessageRowKindHasModal("notification");
  const unread = isInboxMessageUnread(item);

  // Round 15 UAT fix ("Open record" appeared to do nothing): the Inbox panel is a persistent
  // sidebar/full-screen overlay (`AppContainer.tsx:117-128`) whose visibility is driven only by
  // `actionCenterContext.state.isOpen` — nothing before this closed it on navigate, so the order
  // page rendered narrowed beside it (desktop) or fully hidden under it (mobile, `pos: "fixed"`,
  // `w: "100vw"`), reading as "nothing happened". Closing the panel here, alongside the existing
  // modal-close, makes the navigation land visibly regardless of viewport.
  const markRead = () => markReadMutation.mutate({ id: item.id });

  const openLinkedRecord = () => {
    setIsModalOpen(false);
    actionCenter.setIsOpen(false);
    handleInboxNotificationClick({
      link: item.link,
      navigate: link => void orgRouter.push(link),
      markRead,
    });
  };

  /**
   * What this notification offers to DO about itself, decided here and handed to the detail view as a
   * label + handler (round 20). `link` is the only actionable field `InboxListItem` carries today, so
   * a linked row offers "View record" and an unlinked one offers nothing — but the detail view is not
   * built around links, so a future source with a different affordance changes only this expression.
   */
  const detailAction = item.link ? { label: "View record", onAct: openLinkedRecord } : null;

  /**
   * Round 20: reading the notification is what marks it read. Until now the ONLY thing that cleared
   * the unread state was "Open record", which also navigated away — so a user who opened the detail,
   * read it and closed it left it unread, and the badge never went down. Opening the detail is the
   * read signal; the mutation is skipped when it is already read so re-opening never rewrites `readAt`.
   */
  const openDetail = () => {
    setIsModalOpen(true);
    if (unread) {
      markRead();
    }
  };

  return (
    <>
      {/* Round 23: the read/unread variant is the ROW's own surface — a blue tint means unread, there is
          no unread dot, and the colour is blue rather than the severity colour (severity is the avatar's
          job). Round 21's grey-for-read was read backwards in UAT, so the tint stays on the unread side. */}
      <Box
        className={classNames(styles.row, hasModal && styles.notificationRow, unread ? styles.unread : styles.read)}
        role={hasModal ? "button" : undefined}
        tabIndex={hasModal ? 0 : undefined}
        onClick={hasModal ? openDetail : undefined}
        onKeyDown={event => {
          // The inline link inside the row handles its own Enter; only the row itself opens the modal.
          if (!hasModal || event.target !== event.currentTarget) {
            return;
          }
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openDetail();
          }
        }}
      >
        <SeverityAvatar severity={item.severity} size={32} iconSize={17} />
        <div className={styles.rowContent}>
          <div className={styles.titleLine}>
            <Text
              c={unread ? tokens.color.content.strong : tokens.color.content.secondary}
              fw={unread ? 600 : 500}
              fz="md"
              lh={tokens.typography.textLineHeight.md}
              style={{ flex: 1, minWidth: 0 }}
              truncate
            >
              {item.title}
            </Text>
            <Text
              c={tokens.color.content.secondary}
              fz="xs"
              style={{ flexShrink: 0 }}
              title={dayjs(item.createdAt).format("MMM D, h:mm A")}
            >
              {formatInboxRelativeTime(item.createdAt, props.now)}
            </Text>
          </div>
          {item.message && (
            <Text c={tokens.color.content.secondary} fz="sm" lh={tokens.typography.textLineHeight.sm} truncate>
              {item.message}
            </Text>
          )}
          {/* Round 15 UAT fix: the same link `NotificationDetail`'s footer carries, so the record
           *  this notification is about is reachable without opening the modal first. `stopPropagation`
           *  keeps the row's own onClick (open modal) from also firing. */}
          {item.link && (
            <Anchor
              className={styles.rowLink}
              fw={500}
              fz="sm"
              href={item.link}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                openLinkedRecord();
              }}
            >
              View
              <IconArrowRight size={13} />
            </Anchor>
          )}
        </div>
      </Box>
      {hasModal && (
        // The modal is portalled out of the panel, so it carries the Inbox colour variables itself. Built
        // from the `Modal.*` parts rather than the flat `Modal` so the close button sits in the header
        // row beside the title: the flat Modal's header would be an empty band above the content.
        <Modal.Root opened={isModalOpen} shadow={tokens.shadow.xl} size={480} onClose={() => setIsModalOpen(false)}>
          <Modal.Overlay color={tokens.color.content.strong} opacity={0.45} />
          <Modal.Content style={{ ...inboxStyleVars, borderRadius: tokens.radius.lg }}>
            <NotificationDetail item={item} action={detailAction} />
          </Modal.Content>
        </Modal.Root>
      )}
    </>
  );
};

export const InboxMessageRow = (props: InboxMessageRowProps): JSX.Element => {
  // Narrows on the discriminant directly (not `getInboxMessageRowKind`'s derived string) so TypeScript
  // itself proves `item.task` below, no cast needed.
  if (props.item.messageType === "TASK") {
    return <TaskInboxRow item={props.item} onUpdate={props.onUpdate} />;
  }

  return <NotificationInboxRow item={props.item} now={props.now} onOpened={props.onOpened} />;
};
